import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "@/data/content";
import { openLeadModal } from "@/components/LeadModal";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Pricing", href: "#pricing" },
  { label: "Work", href: "#portfolio" },
  { label: "Social", href: "#social" },
  { label: "Contact", href: "#contact" },
];

export const scrollToSection = (href) => {
  if (window.__lenis) window.__lenis.scrollTo(href, { offset: -70, duration: 1.4 });
  else document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    scrollToSection(href);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between">
        <a href="#top" onClick={(e) => go(e, "#top")} data-testid="brandify-nav-logo" className="flex items-center" aria-label="Brandify home">
          <img src="/assets/brandify-logo.png" alt="Brandify" className="h-9 sm:h-11 w-auto" />
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={(e) => go(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-call-btn"
            onClick={() => openLeadModal()}
            className="hidden sm:inline-flex btn-gradient text-white text-sm font-semibold px-5 py-2.5 rounded-full"
          >
            Book a Free Strategy Call
          </button>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              {LINKS.map((l) => (
                <a key={l.href} href={l.href} onClick={(e) => go(e, l.href)} className="text-base text-zinc-300 hover:text-white py-3 min-h-[44px] flex items-center border-b border-white/5">
                  {l.label}
                </a>
              ))}
              <button
                data-testid="nav-mobile-book-call-btn"
                onClick={() => { setOpen(false); openLeadModal(); }}
                className="btn-gradient text-white text-sm font-semibold px-5 py-3 rounded-full w-full"
              >
                Book a Free Strategy Call
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
